/*
 * Service to store user selected configuration.
 *
 * @Author: Jon Lucas
 * @Date: 2017-10-18 15:44:26
 * @Last Modified by: Jon Lucas
 * @Last Modified time: 2017-12-22 12:33:24
 */
import {Injectable} from "@angular/core";
import {Subject} from "rxjs/Subject";
import {Observable} from "rxjs/Observable";
import {AppService} from "./app.service";
import {ConfDataType} from "./conf-data-type";

@Injectable()
export class ConfDataService {

    public readonly TAG: string = "[ConfDataService]";

    /**
     * Default values are stored here.
     * Overrided by LocalStorage values.
     */
    private defaultconfdata: {[id: string]: ConfDataType} =
        // default configuration
        {
            "roof-material": {
                id: "roof-material", /*values: ["toiture-tuile"],*/
                apiurl: AppService.ROOF_MATERIAL_TYPES_URL
            },
            "walls-material": {
                id: "walls-material", /*values: ["pierres"],*/
                options: ["white"],
                apiurl: AppService.WALLS_MATERIAL_TYPES_URL
            },

            "project-type": {
                id: "project-type",
                options: ["facade"],
                apiurl: AppService.PROJECT_TYPES_URL},
            "project-type-valid": {id: "project-type-valid"},
            "profile-type": {
                id: "profile-type", /*values: ["lg28"],*/
                options: ["blanc"],
                apiurl: AppService.PROFILE_TYPES_URL
            },
            "descente-type": {
                id: "descente-type", /*values: ["lg38"],*/
                options: ["blanc"],
                apiurl: AppService.DESCENTE_TYPES_URL
            },
            "belriv-type": {id: "belriv-type", options: ["blanc"], apiurl: AppService.BELRIV_TYPES_URL},

            "roof-type": {id: "roof-type", apiurl: AppService.ROOF_TYPES_URL},
            "surelevation-type": {id: "surelevation-type", apiurl: AppService.SURELEVATION_TYPES_URL},
            "roof-dimensions": {id: "roof-dimensions", apiurl: AppService.ROOF_DIMENSIONS_URL},
            "roof-dimensions-valid": {id: "roof-dimensions-valid"},
            "roof-dimensions-errors": {id: "roof-dimensions-errors"},
            "surelevation-dimensions": {id: "surelevation-dimensions", apiurl: AppService.SURELEVATION_DIMENSIONS_URL},
            "surelevation-dimensions-valid": {id: "surelevation-dimensions-valid"},

            "installation-type": {id: "installation-type", apiurl: AppService.INSTALLATION_TYPES_URL},
            "crochet-type": {id: "crochet-type", apiurl: AppService.CROCHET_TYPES_URL},
            "belriv-dimensions": {id: "belriv-dimensions", apiurl: AppService.BELRIV_DIMENSIONS_URL},
            "belriv-dimensions-valid": {id: "belriv-dimensions-valid"},
            "wall-profile-type": {id: "wall-profile-type", apiurl: AppService.WALL_PROFILE_TYPES_URL},
            "accessories-type": {id: "accessories-type", apiurl: AppService.ACCESSORIES_TYPES_URL},
            "accessories-type-valid": {id: "accessories-type-valid"},

            "contact-form": {id: "contact-form", apiurl: AppService.CONTACT_FORM_URL},
            "contact-form-valid": {id: "contact-form-valid"}
        };

    public confdata: {[id: string]: ConfDataType};

    public subject: Subject<ConfDataType> = new Subject<ConfDataType>();

    public observable: Observable<ConfDataType> = this.subject.asObservable();

    constructor() {

        this.confdata = JSON.parse(JSON.stringify(this.defaultconfdata));

        if (typeof (Storage) !== "undefined") {
            // Override confdata default configuration by LocalStorage stored values.
            // localStorage.clear();
            this.refreshConfData();
        } else {
            console.error(this.TAG, "Sorry! No Web Storage support...");
        }
    }

    public refreshConfData() {
        if (localStorage.length > 0) {
            for (const key in this.confdata) {
                if (!localStorage.getItem(key)) {
                    // Reset config if previous value
                    this.confdata[key] = this.defaultconfdata[key];
                    continue;
                }
                this.confdata[key] = JSON.parse(localStorage.getItem(key));
            }
        }
    }

    /**
     * Update selected values for a given json id
     *
     * @param {string}   pId     [description]
     * @param {string[]} pValues [description]
     */
    public updateElementValues(pId: string, pValues: any[]): void {
        console.log(this.TAG, "updateElementValues()", pId, pValues);

        this.confdata[pId].values = pValues;
        localStorage.setItem(pId, JSON.stringify(this.confdata[pId]));

        // Dispatches next to update observable listening components
        this.subject.next(this.confdata[pId]);

        switch (pId) {
            // Reset profile-type color to default.
            // Reset descente-type if we change profile model.
            // This action removes descente from the threedview.
            case "profile-type" :
                this.updateElementOptions("profile-type", ["blanc"]);
                this.updateElementValues("descente-type", undefined);
                break;

            // Reset descente-type color to default.
            case "descente-type" :
                this.updateElementOptions("descente-type", ["blanc"]);
                break;

            // Reset profile-type and descente-type if we select projet-debord project-type.
            // This action removes profile adescente from the threedview.
            case "project-type" :
                this.updateElementValues("project-type-valid", undefined);
                this.updateElementValues("roof-dimensions-valid", undefined);
                if (pValues[0] === "projet-debord") {
                    this.updateElementValues("profile-type", undefined);
                    this.updateElementValues("descente-type", undefined);
                } else if (pValues[0] === "projet-goutiere") {
                    // Reset cote-l2 value / #28633
                    this.updateElementOptions("project-type", undefined);
                }
                break;

            // Reset roof-dimensions and roof-dimensions-valid if we change roof-type,
            // to invalidate form.
            case "roof-type" :
                this.updateElementValues("roof-dimensions", undefined);
                this.updateElementValues("roof-dimensions-valid", undefined);
                this.updateElementValues("roof-dimensions-errors", undefined);
                break;

            // Reset surelevation-dimensions and surelevation-dimensions-valid if we change roof-type,
            // to invalidate form.
            case "surelevation-type" :
                this.updateElementValues("surelevation-dimensions", undefined);
                this.updateElementValues("surelevation-dimensions-valid", undefined);
                break;

            // Reset installation-type if we change roof-material,
            // to invalidate form.
            case "roof-material" :
                this.updateElementValues("installation-type", undefined);
                break;

            default:
                break;
        }
    }

    /**
     * Update selected optional values for a given json id
     *
     * @param {string}   pId     [description]
     * @param {string[]} pValues [description]
     */
    public updateElementOptions(pId: string, pValues: any[]): void {
        console.log(this.TAG, "updateElementOptions()", pId, pValues);

        this.confdata[pId].options = pValues;
        localStorage.setItem(pId, JSON.stringify(this.confdata[pId]));

        console.log(this.TAG, this.confdata[pId]);

        // Dispatches next to emit an update to every component listening to the observable
        this.subject.next(this.confdata[pId]);

        switch (pId) {
            // Reset belriv-dimensions and valid flag on project-type options change.
            case "project-type" :
                this.updateElementValues("belriv-dimensions", undefined);
                this.updateElementValues("belriv-dimensions-valid", undefined);
                break;

            default:
                break;
        }
    }

    /**
     * Update selected errors values for a given json id
     *
     * @param {string}   pId     [description]
     * @param {string[]} pValues [description]
     */
    public updateElementErrors(pId: string, pErrors: any[]): void {
        console.log(this.TAG, "updateElementErrors()", pId, pErrors);

        this.confdata[pId].errors = pErrors;
        localStorage.setItem(pId, JSON.stringify(this.confdata[pId]));

        console.log(this.TAG, this.confdata[pId]);

        // Dispatches next to emit an update to every component listening to the observable
        this.subject.next(this.confdata[pId]);
    }

    /**
     * Get element's values array (typically selected element's json id) for passed parameter (parent/section json id).
     *
     * @param  {string} pId [description]
     * @return {string}     [description]
     */
    public getElementValues(pId: string): any[] {
        if (!this.confdata[pId]) {
            return;
        }

        return this.confdata[pId].values || undefined;
    }

    /**
     * Get element's options array (typically selected element's json id) for passed parameter (parent/section json id).
     *
     * @param  {string} pId [description]
     * @return {string}     [description]
     */
    public getElementOptions(pId: string): any[] {
        if (!this.confdata[pId]) {
            return;
        }

        return this.confdata[pId].options || undefined;
    }

    /**
     * Get api url for a given json parent/section id
     *
     * @param  {string} pId [description]
     * @return {string}     [description]
     */
    public getApiUrl(pId: string): string {
        if (!this.confdata[pId]) {
            return;
        }

        return this.confdata[pId].apiurl || undefined;
    }

    public removeValue(pId: string): void {
        console.log(this.TAG, "removeValue()", pId);
        delete this.confdata[pId].values;
        delete this.confdata[pId].options;
        localStorage.setItem(pId, JSON.stringify(this.confdata[pId]));

        console.log(this.TAG, this.confdata[pId]);

        // Dispatches next to emit an update to every component listening to the observable
        this.subject.next(this.confdata[pId]);
    }

    public resetToDefault(): void {
        let authorizedToken = null;
        if(localStorage.getItem('authorized') !== null) {
            authorizedToken = localStorage.getItem('authorized');
        }
        localStorage.clear();
        if(authorizedToken !== null) {
            localStorage.setItem('authorized', authorizedToken);
        }
        this.confdata = JSON.parse(JSON.stringify(this.defaultconfdata));

    }
}



// WEBPACK FOOTER //
// ./src/app/conf-data.service.ts